import { AiDifficulty } from './AiDifficulty';
import { PlayerId, getRandomItem, getAllAxisIterators, BoardIterator, hasNeighbor, hasAnyPlayedNeighbor } from '../helpers/AiHelper';

export default (board: number[][], victoryCondition: number, difficulty: number): { x: number, y: number } | null => {
    let difficultyString = "";
    switch (difficulty) {
        case AiDifficulty.Hard: difficultyString = "HARD"; break;
        case AiDifficulty.Medium: difficultyString = "MEDIUM"; break;
        case AiDifficulty.Easy: difficultyString = "EASY"; break;
    }
    console.log(`==[ START STEP; Difficulty: ${difficultyString} ]==`);
    
    const startTime = performance.now();
    const result = getNextAiMove(board, victoryCondition, difficulty);
    const deltaTime = performance.now() - startTime;
    
    if (result === null) {
        console.error('AI: No available moves found!');
        return null;
    } else {
        console.log(`AI: ${result.log} (${deltaTime.toFixed(0)} ms)`);
        return {
            x: result.x - 1, // (Corrigate for padding)
            y: result.y - 1,
        };
    }
}

const getNextAiMove = (board: number[][], victoryCondition: number, difficulty: number): {log: string, x: number, y: number} | null => {
    // Possible moves by AI.
    const attackList = getPossibleMoves(board, victoryCondition, PlayerId.AI);
    logPossibleMoves(attackList, "ATTACK");
    
    // Find instant wins.
    for (let i = 0; i < attackList.length; i++) {
        const move = attackList[i];
        if (move.stepsToWin === 1) {
            return {
                log: "Instant win.",
                x: move.x,
                y: move.y,
            };
        }
    }
    
    // Possible moves by Player.
    const defendList = getPossibleMoves(board, victoryCondition, PlayerId.Human);
    const humanLeastStepsToWin = getLeastStepsToWin(defendList);
    logPossibleMoves(defendList, "DEFEND");
    
    // Panic defense 1 (fours).
    if (humanLeastStepsToWin === 1) {
        const movesList = getExactStepsToWinWithMostStepsToCounter(defendList, 1);
        const move = getRandomItem(movesList);
        if (move) {
            return {
                log: formatLog("Panic defense 1.", move, movesList.length) + (movesList.length > 1 ? " (AI is done for, unless player messes this up)" : ""),
                x: move.x,
                y: move.y,
            };
        }
        console.error("Panic defense 1 error.");
        return null; // This should not happen.
    }
    
    switch (difficulty) {
        case AiDifficulty.Hard:
        case AiDifficulty.Medium: {
            const isHard = difficulty === AiDifficulty.Hard;
            
            // Panic defense 2 (threes).
            if (humanLeastStepsToWin === 2) {
                // Human moves with more than 1 steps to counter (open threes).
                const humanMovesList = getExactStepsToWinWithMostStepsToCounter(defendList, 2).filter(move => move.stepsToCounter >= 2);
                
                // If AI has a better one, then prefer that.
                if (isHard) {
                    const movesList = getExactStepsToWinWithMostStepsToCounter(attackList, 2).filter(move => move.stepsToCounter >= 2);
                    const move = getRandomItem(movesList);
                    if (move) {
                        return {
                            log: formatLog(`Player has ${humanMovesList.length} ace move(s), but AI has better.`, move, movesList.length),
                            x: move.x,
                            y: move.y,
                        };
                    }
                }
                
                // Back to panic defense.
                {
                    const move = getRandomItem(humanMovesList);
                    if (move) {
                        return {
                            log: formatLog("Panic defense 2.", move, humanMovesList.length),
                            x: move.x,
                            y: move.y,
                        };
                    }
                }
                
                // Human is still 2 steps from winning, so try regular defense.
                {
                    const movesList = getExactStepsToWinWithMostStepsToCounter(defendList, 2);
                    const move = getRandomItem(movesList);
                    if (move) {
                        return {
                            log: formatLog("Defend.", move, movesList.length),
                            x: move.x,
                            y: move.y,
                        };
                    }
                    console.error("Defend error.");
                    return null; // This should not happen.
                }
            }
            
            // Preemptive defense (complex twos).
            if (isHard && humanLeastStepsToWin === 3) {
                // Ignore single axis twos.
                const movesList = getExactStepsToWinWithMostStepsToCounter(defendList, 3).filter(move => move.stepsToCounter >= 4);
                const move = getRandomItem(movesList);
                if (move) {
                    return {
                        log: formatLog("Defend agains complex twos.", move, movesList.length),
                        x: move.x,
                        y: move.y,
                    };
                }
            }
            
            // Player is at bay, attack!
            const aiLeastStepsToWin = getLeastStepsToWin(attackList);
            if (aiLeastStepsToWin) {
                const movesList = getExactStepsToWinWithMostStepsToCounter(attackList, aiLeastStepsToWin);
                const move = getRandomItem(movesList);
                if (move) {
                    return {
                        log: formatLog("Attack.", move, movesList.length),
                        x: move.x,
                        y: move.y,
                    };
                }
            }
            break;
        }
        case AiDifficulty.Easy:
        {
            // Defense
            if (humanLeastStepsToWin <= 2)
            {
                const movesList = getMovesWithExactStepsToWin(defendList, humanLeastStepsToWin);
                const move = getRandomItem(movesList);
                if (move) {
                    return {
                        log: formatLog("Defend.", move, movesList.length),
                        x: move.x,
                        y: move.y,
                    };
                }
            }
            
            // Attack
            const aiLeastStepsToWin = getLeastStepsToWin(attackList);
            if (aiLeastStepsToWin) {
                const movesList = getMovesWithExactStepsToWin(attackList, aiLeastStepsToWin);
                const move = getRandomItem(movesList);
                if (move) {
                    return {
                        log: formatLog("Attack.", move, movesList.length),
                        x: move.x,
                        y: move.y,
                    };
                }
            }
            break;
        }
    }
    
    // Random.
    const randomMove = aiRandomExpansion(board);
    if (randomMove !== null) {
        return {
            log: "Random.",
            x: randomMove.x,
            y: randomMove.y,
        };
    }
    
    // No possible moves left on the board.
    return null;
}


//
// RANDOM AI STEP
//
const aiRandomExpansion = (board: number[][]): { x: number, y: number } | null => {
    const width = board[0].length - 1;
    const height = board.length - 1;
    const candidates: {x: number, y: number}[] = [];
    
    for (let y = 1; y < height; y++) {
        for (let x = 1; x < width; x++) {
            if (board[y][x] === PlayerId.Unset) {
                if (hasAnyPlayedNeighbor(board, x, y)) {
                    candidates.push({x: x, y: y});
                }
            }
        }
    }
    
    // Pick one randomly
    return getRandomItem(candidates);
}


//
// HELPERS
//
const getLeastStepsToWin = (movesList: PossibleMoveType[]): number => {
    return movesList.length
        ? Math.min.apply(Math, movesList.map((item) => item.stepsToWin))
        : 100;
}

const getExactStepsToWinWithMostStepsToCounter = (movesList: PossibleMoveType[], stepsToWin: number): PossibleMoveType[] => {
    if (movesList.length) {
        const exactMoves = movesList.filter(move => move.stepsToWin === stepsToWin);
        const mostStepsToCounter = Math.max.apply(Math, exactMoves.map((item) => item.stepsToCounter));
        return exactMoves.filter(move => move.stepsToCounter === mostStepsToCounter);
    }
    return [];
}

const getMovesWithExactStepsToWin = (movesList: PossibleMoveType[], stepsToWin: number): PossibleMoveType[] => {
    return movesList.length
        ? movesList.filter(move => move.stepsToWin === stepsToWin)
        : [];
}


//
// COUNTING STRIKES & POSSIBLE MOVES
//
type PossibleMoveType = {
    x: number;
    y: number;
    stepsToWin: number;     // Number of steps it takes to win with this move, including this move.
    stepsToCounter: number; // Number of steps it takes for opponent to block strike on all involved axes.
    axes: number;           // Number of axes involved [probably irrelevant]
}

type StrikeType = {
    length: number;
    bothSideOpen: boolean;
}

// Count length of strike, starting at index.
// If strike is enclosed and can never reach the length of victoryCondition, 0 is returned.
const countStrikeLength = (iterator: BoardIterator, victoryCondition: number): StrikeType | null => {
    const playerId = iterator.get(0);
    let count = 1;
    let freeSpaceLeft = 0;
    let freeSpaceRight = 0;
    let i: number;
    
    // Count left
    // Count consecutive player marks
    for (i = -1; iterator.get(i) === playerId; i--) {
        count++;
    }
    // Count available space
    for (; i > -victoryCondition && iterator.get(i) === PlayerId.Unset; i--) {
        freeSpaceLeft++;
    }
    
    // Count right
    // Count consecutive player marks
    for (i = 1; iterator.get(i) === playerId; i++) {
        count++;
    }
    // Count available space
    for (; i < victoryCondition && iterator.get(i) === PlayerId.Unset; i++) {
        freeSpaceRight++;
    }
    
    if ((count + freeSpaceLeft + freeSpaceRight) >= victoryCondition) {
        const stepsToWin = Math.max(1, victoryCondition - count);
        return {
            length: count,
            // Only consider a side open, if by blocking the OTHER side, it still has space for a win
            bothSideOpen: freeSpaceLeft >= stepsToWin && freeSpaceRight >= stepsToWin
        };
    } else {
        // No space to win, not valid step.
        return null;
    }
}

const getPossibleMoves = (board: number[][], victoryCondition: number, playerId: number): PossibleMoveType[] => {
    const width = board[0].length - 1;
    const height = board.length - 1;
    const list: PossibleMoveType[] = [];
    
    for (let y = 1; y < height; y++) {
        for (let x = 1; x < width; x++) {
            if (board[y][x] === PlayerId.Unset) {
                // Check for at least one neighbor of same player ID.
                if (!hasNeighbor(board, x, y, playerId)) {
                    continue;
                }
                
                // Set cell with move.
                board[y][x] = playerId;
                
                //  Get all possible, then filter out all the relevant and valid moves
                const strikes = (
                    getAllAxisIterators(board, x, y)
                    .map(it => countStrikeLength(it, victoryCondition))
                    .filter((strike): strike is StrikeType => {
                        return strike !== null && strike.length >= 2;
                    })
                );
                
                // Check all valid moves
                if (strikes.length > 0) {
                    list.push({
                        x,
                        y,
                        stepsToWin: (strikes
                            .map(strike => Math.max(1, victoryCondition - strike.length + 1)) // Steps to win, including this move
                            .reduce((smallest, stepsToWin) => Math.min(smallest, stepsToWin), 1000) // Use the smallest count
                        ),
                        stepsToCounter: (strikes
                            .reduce((sum, strike) => sum + (strike.bothSideOpen ? 2 : 1), 0)
                        ),
                        axes: strikes.length
                    });
                }
                
                // Reset cell
                board[y][x] = 0;
            }
        }
    }
    return list;
}


//
// LOGGING
//
const formatLog = (title: string, move: PossibleMoveType, movePoolSize: number): string => (
    `${title} -- Chosen from ${movePoolSize} move${movePoolSize > 1 ? "s" : ""}: [steps to win: ${move.stepsToWin}; steps to counter: ${move.stepsToCounter}]`
);

const logPossibleMoves = (moves: PossibleMoveType[], label: string) => {
    moves.forEach(move => {
        let remark = "";
        if (move.stepsToWin === 1) {
            remark = "One step from victory!";
            
        } else if (move.stepsToWin <= move.stepsToCounter) {
            remark = "More steps to counter than to win!";
        }
        
        console.log(`-- ${label}[${move.x}, ${move.y}]; steps to win: ${move.stepsToWin}; steps to counter: ${move.stepsToCounter}; ${remark}`);
    })
};
